import { sansSerifStyles } from "@pathwright/blocks/src/ui/typography"
import styled, { css } from "styled-components"

type CheckedColorProps = { $checkedColor?: string }
type DisabledProps = { $disabled?: boolean }
type ExpandedProps = { $expanded?: boolean }
type ScaleCountProps = { $scaleCount: number }
type SelectedProps = { $selected?: boolean }

// Switch before a seven-point horizontal scale becomes cramped, leaving the
// existing vertical card UI for narrower containers.
export const LIKERT_MOBILE_BREAKPOINT = 850

const LIKERT_SCALE_OPTION_HEIGHT = 40
const LIKERT_SCALE_OPTION_RADIUS = 20
// Opaque gray-on-white so a translucent fill can't punch through dark block
// backgrounds. Same look as a 16% #2a2e33 overlay on the white scale.
const LIKERT_SELECTED_FILL = "color-mix(in srgb, #2a2e33 16%, white)"
const LIKERT_FOCUS_INSET_SHADOW = "inset 0 0 6px 3px rgba(42, 46, 51, 0.2)"

const bodyTypography = css`
  ${sansSerifStyles({ size: 16 })}
  color: #2a2e33;
  line-height: 1.4;
`

const desktopBodyTypography = css`
  ${sansSerifStyles({ size: 16 })}
  color: #2a2e33;
  line-height: 1.45;
`

const desktopStatementTypography = css`
  ${sansSerifStyles({ size: 22 })}
  color: var(--styled-block-color, #2a2e33);
  line-height: 1.35;
  font-weight: 600;
`

const scaleTypography = css`
  ${sansSerifStyles({ size: 14 })}
  color: #2a2e33;
  line-height: 1.3;
`

const scaleNumberLabel = css`
  ${scaleTypography}
  min-width: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
`

const visuallyHiddenControl = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

// Radios stay in the tree for keyboard and AT; selection is the row/segment fill.
const radioButtonInputStyles = css`
  .RadioButton,
  .RadioButton > span {
    ${visuallyHiddenControl}
  }
`

export const StatementListContainer = styled.div<{ $mobile?: boolean }>`
  --likert-statement-gap: ${(p) => (p.$mobile ? "0.4rem" : "0")};
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--likert-statement-gap);
  container-name: likert-scale;
  container-type: inline-size;
  padding-top: 20px;

  .ListItemContainer {
    gap: var(--likert-statement-gap) !important;
  }
`

export const DesktopStatementCard = styled.div<{ $draft?: boolean }>`
  ${desktopBodyTypography}
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
  padding: 1rem 0.4rem;
  border-bottom: ${(p) =>
    p.$draft ? "0" : "1px solid rgba(42, 46, 51, 0.12)"};

  .BlockText,
  .BlockText p,
  .ContentText,
  .ContentText p {
    ${desktopBodyTypography}
    margin: 0;
  }

  .LikertDesktopStatementActions {
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease;
  }

  &:hover .LikertDesktopStatementActions,
  &:focus-within .LikertDesktopStatementActions {
    opacity: 1;
    pointer-events: auto;
  }
`

export const AddStatementButton = styled.button`
  ${desktopBodyTypography}
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 50px;
  margin: 1rem 0 0;
  padding: 0.65rem 1rem;
  border: 1px dashed
    color-mix(in srgb, var(--styled-block-color, #2a2e33) 35%, transparent);
  border-radius: 5px;
  color: #666;
  background: color-mix(
    in srgb,
    var(--styled-block-color, #2a2e33) 5%,
    transparent
  );
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background-color 120ms ease, box-shadow 120ms ease;

  > .Pathicon {
    display: block;
    flex: 0 0 1em;
    width: 1em;
    height: 1em;
  }

  > span {
    display: block;
    line-height: 1;
  }

  &:hover,
  &:focus-visible {
    background: color-mix(
      in srgb,
      var(--styled-block-color, #2a2e33) 10%,
      transparent
    );
    outline: none;
    box-shadow: 0 0 0 2px
      color-mix(in srgb, var(--styled-block-color, #2a2e33) 25%, transparent);
  }
`

export const AddStatementReveal = styled.div<ExpandedProps>`
  display: grid;
  grid-template-rows: ${(p) => (p.$expanded ? "1fr" : "0fr")};
  opacity: ${(p) => (p.$expanded ? 1 : 0)};
  transition: grid-template-rows 180ms ease, opacity 160ms ease;

  > div {
    min-height: 0;
    overflow: ${(p) => (p.$expanded ? "visible" : "hidden")};
  }
`

export const DesktopStatementHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  min-width: 0;

  > .BlockText,
  > .ContentText {
    flex: 1 1 auto;
    min-width: 0;
  }

  .BlockText,
  .BlockText p,
  .ContentText,
  .ContentText p,
  .ql-editor,
  .ql-editor p,
  .ql-editor.ql-blank::before {
    ${desktopStatementTypography}
    margin: 0;
  }
`

export const DesktopStatementActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  flex: 0 0 auto;
  color: var(--styled-block-color, #2a2e33);

  button {
    flex: 0 0 auto;
    color: inherit !important;
  }

  .Pathicon {
    color: var(--styled-block-color, #2a2e33) !important;
  }

  button:hover,
  button:focus-visible,
  [role="button"]:hover,
  [role="button"]:focus-visible {
    background: color-mix(
      in srgb,
      var(--styled-block-color, #2a2e33) 12%,
      transparent
    ) !important;
  }
`

export const DesktopScale = styled.div<
  ScaleCountProps & {
    $scaleEditorOpen?: boolean
    $showEditTrigger?: boolean
  }
>`
  --scale-count: ${(p) => p.$scaleCount};
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 0.15rem;

  .LikertDesktopScaleVisual {
    transition: opacity 120ms ease;
  }

  .LikertEditSharedScaleTrigger {
    position: absolute;
    top: calc(0.15rem + ${LIKERT_SCALE_OPTION_HEIGHT / 2}px);
    left: 50%;
    z-index: 2;
    opacity: 0;
    pointer-events: none;
    transform: translate(-50%, -50%);
    transition: opacity 120ms ease;
  }

  &:hover .LikertEditSharedScaleTrigger,
  &:focus-within .LikertEditSharedScaleTrigger,
  .LikertEditSharedScaleTrigger[data-state="open"] {
    opacity: 1;
    pointer-events: auto;
  }

  ${(p) =>
    p.$showEditTrigger &&
    css`
      &:hover .LikertDesktopScaleVisual,
      &:focus-within .LikertDesktopScaleVisual {
        opacity: 0.4;
      }
    `}

  ${(p) =>
    p.$scaleEditorOpen &&
    css`
      .LikertDesktopScaleVisual {
        opacity: 0.4;
      }
    `}
`

export const DesktopScaleSegments = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--scale-count), minmax(0, 1fr));
  min-width: 0;
  width: 100%;

  > :first-child {
    border-left-width: 1px;
    border-radius: ${LIKERT_SCALE_OPTION_RADIUS}px 0 0
      ${LIKERT_SCALE_OPTION_RADIUS}px;
  }

  > :last-child {
    border-radius: 0 ${LIKERT_SCALE_OPTION_RADIUS}px
      ${LIKERT_SCALE_OPTION_RADIUS}px 0;
  }
`

export const DesktopScaleLabels = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--scale-count), minmax(0, 1fr));
  align-items: start;
  min-width: 0;
  width: 100%;
`

export const DesktopScaleSegment = styled.label<
  CheckedColorProps &
    DisabledProps & {
      $editMode?: boolean
      $selected?: boolean
    }
>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: ${LIKERT_SCALE_OPTION_HEIGHT}px;
  border: 1px solid
    ${(p) =>
      p.$editMode
        ? "color-mix(in srgb, var(--styled-block-color, #2a2e33) 48%, transparent)"
        : "rgba(42, 46, 51, 0.4)"};
  border-left-width: 0;
  color: ${(p) =>
    p.$editMode ? "var(--styled-block-color, #2a2e33)" : "#2a2e33"};
  background: ${(p) =>
    p.$selected
      ? p.$editMode
        ? "color-mix(in srgb, var(--styled-block-color, #2a2e33) 22%, transparent)"
        : LIKERT_SELECTED_FILL
      : p.$editMode
      ? "color-mix(in srgb, var(--styled-block-color, #2a2e33) 7%, transparent)"
      : "white"};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  transition: color 120ms ease, background-color 120ms ease,
    box-shadow 120ms ease;

  &:focus-within:has(input:focus-visible) {
    z-index: 1;
    outline: none;
    box-shadow: ${LIKERT_FOCUS_INSET_SHADOW};
  }

  input {
    ${visuallyHiddenControl}
    appearance: none;
    -webkit-appearance: none;

    &:focus-visible {
      outline: none;
    }
  }

  span {
    ${scaleNumberLabel}
    font-weight: ${(p) => (p.$selected ? 700 : 400)};
  }
`

export const DesktopScaleLabel = styled.div<SelectedProps>`
  ${scaleTypography}
  min-width: 0;
  padding: 0.35rem 0.2rem 0;
  color: var(--styled-block-color, #2a2e33);
  font-weight: ${(p) => (p.$selected ? 700 : 400)};
  overflow-wrap: anywhere;
  text-align: center;
`

export const EditSharedScaleButton = styled.button`
  ${sansSerifStyles({ size: 12 })}
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-height: 26px;
  padding: 0.25rem 0.65rem;
  border: 1px solid rgba(42, 46, 51, 0.12);
  border-radius: 999px;
  color: #2a2e33;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 8px rgba(42, 46, 51, 0.14);
  cursor: pointer;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background: white;
    outline: none;
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.3);
  }
`

export const ScaleItemEditor = styled.div`
  ${scaleTypography}
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  width: 100%;
  min-width: 0;

  .chakra-input,
  .chakra-textarea {
    ${scaleTypography}
    width: 100%;
    min-width: 0;
    height: auto;
    min-height: 44px;
    padding: 0.2rem !important;
    text-align: center;
    resize: none;
    field-sizing: content;
    overflow: hidden;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    border: 0 !important;
    border-radius: 5px;
    background: transparent !important;
    box-shadow: none !important;

    &:focus {
      background: white !important;
      box-shadow: 0 0 0 1px #6366f1 !important;
    }
  }
`

export const NumericInput = styled.input`
  ${sansSerifStyles({ size: 12 })}
  width: 36px;
  height: 22px;
  padding: 0 0.2rem;
  text-align: center;
  border: 0;
  border-radius: 4px;
  color: #2a2e33;
  background: transparent;
  font-weight: 700;
  appearance: textfield;
  -moz-appearance: textfield;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    margin: 0;
    appearance: none;
    -webkit-appearance: none;
  }

  &:focus {
    outline: none;
    background: white;
    box-shadow: 0 0 0 1px #6366f1;
  }
`

export const ScaleMenuPosition = styled.div`
  position: absolute;
  top: 0.15rem;
  right: 0.15rem;
  z-index: 3;
`

export const StatementEditorMain = styled.div<{ $desktopStatement?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;

  .ql-editor,
  .ql-editor p,
  .ql-editor.ql-blank::before {
    ${desktopBodyTypography}
    margin: 0;
  }

  .ql-toolbar.ql-snow {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }

  .ql-container.ql-snow {
    min-height: 58px;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  ${(p) =>
    p.$desktopStatement &&
    css`
      .ql-editor,
      .ql-editor p,
      .ql-editor.ql-blank::before,
      .BlockText,
      .BlockText p,
      .ContentText,
      .ContentText p {
        ${desktopStatementTypography}
        margin: 0;
      }
    `}
`

export const DragHandle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  color: rgba(42, 46, 51, 0.55);
  cursor: grab;
  // Defer touch/drag to JS
  touch-action: none;

  &:hover,
  &:focus-visible,
  &[data-state="open"] {
    color: #2a2e33;
    background: rgba(42, 46, 51, 0.08);
    outline: none;
  }
`

export const MobileScaleScroller = styled.div`
  position: relative;
  display: flex;
  gap: 0.35rem;
  width: 100%;
  padding: 0rem 1px 0rem;
  margin-bottom: 0.5rem;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  touch-action: pan-x;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;

  &::after {
    content: "";
    position: sticky;
    right: -1px;
    flex: 0 0 18px;
    width: 18px;
    margin-left: -18px;
    pointer-events: none;
    background: linear-gradient(
      to right,
      transparent,
      rgba(255, 255, 255, 0.9)
    );
  }
`

export const MobileScaleCard = styled.div`
  position: relative;
  flex: 0 0 clamp(68px, 23vw, 104px);
  max-width: 104px;
  min-height: 68px;
  padding: 0.35rem 0.25rem;
  border: 1px solid rgba(42, 46, 51, 0.14);
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.55);
  scroll-snap-align: start;
`

export const MobileStatementCard = styled.div<{ $clickable?: boolean }>`
  ${bodyTypography}
  position: relative;
  border: 1px solid rgba(42, 46, 51, 0.14);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.55);
  overflow: hidden;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  // Quill's bubble toolbar may extend outside the statement header. Release
  // overflow only while the editor or one of its toolbar controls has focus;
  // the expandable response area continues to handle its own clipping.
  &:focus-within {
    z-index: 1;
    overflow: visible;
  }

  .ql-editor,
  .ql-editor p,
  .ql-editor.ql-blank::before {
    ${bodyTypography}
  }
`

export const MobileStatementHeader = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-height: 32px;
  padding: 0.45rem;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  .BlockText,
  .BlockText p,
  .ContentText,
  .ContentText p {
    ${bodyTypography}
    margin: 0;
  }
`

export const MobileStatementText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`

export const MobileAnswers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0.35rem 0.75rem 0.75rem;
`

export const MobileAnswer = styled.label<DisabledProps & SelectedProps>`
  ${scaleTypography}
  position: relative;
  display: grid;
  grid-template-columns: 34px 1fr;
  align-items: center;
  gap: 0.4rem;
  min-height: 32px;
  padding: 0 0.5rem 0 0;
  border-radius: 8px;
  background: transparent;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  transition: box-shadow 120ms ease;
  ${radioButtonInputStyles}

  &:focus-within:has(:focus-visible) {
    outline: none;
    box-shadow: ${LIKERT_FOCUS_INSET_SHADOW};
  }

  .LikertScaleNumericValue {
    ${scaleNumberLabel}
    justify-self: start;
    font-weight: ${(p) => (p.$selected ? 700 : 400)};
  }

  .LikertScaleOptionLabel {
    font-weight: ${(p) => (p.$selected ? 700 : 400)};
  }
`

export const MobileVerticalScaleSegment = styled.span<
  SelectedProps & {
    $first?: boolean
    $last?: boolean
  }
>`
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  border: 1px solid rgba(42, 46, 51, 0.4);
  border-top-width: ${(p) => (p.$first ? "1px" : "0")};
  border-radius: ${(p) =>
    p.$first ? "17px 17px 0 0" : p.$last ? "0 0 17px 17px" : "0"};
  background: ${(p) => (p.$selected ? LIKERT_SELECTED_FILL : "white")};
  pointer-events: none;

  .LikertScaleNumericValue {
    ${scaleNumberLabel}
    font-weight: ${(p) => (p.$selected ? 700 : 400)};
  }
`

export const MobileExpandable = styled.div<ExpandedProps>`
  display: grid;
  grid-template-rows: ${(p) => (p.$expanded ? "1fr" : "0fr")};
  opacity: ${(p) => (p.$expanded ? 1 : 0)};
  transition: grid-template-rows 180ms ease, opacity 160ms ease;

  > div {
    min-height: 0;
    overflow: hidden;
  }
`

export const CollapsedAnswerSummary = styled.label<CheckedColorProps>`
  ${scaleTypography}
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  margin: 0 0.75rem 0.65rem 12px;
  color: rgba(42, 46, 51, 0.8);
  ${radioButtonInputStyles}
`

export const MobileSelectedScale = styled.div<ScaleCountProps>`
  --scale-count: ${(p) => p.$scaleCount};
  flex: 0 1 150px;
  width: 150px;
  max-width: 55%;
  min-width: 0;

  ${DesktopScaleSegment} {
    height: 20px;
    cursor: inherit;
    pointer-events: none;
  }

  ${DesktopScaleSegment} span {
    font-size: 10px;
    line-height: 1;
  }
`

export const MobileSelectedScaleLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 50%;
  // overflow: hidden;
  // text-overflow: ellipsis;
  // white-space: nowrap;
  font-weight: 700;
`

export const MobileEditScaleTrigger = styled.div`
  align-self: center;
  display: flex;
  justify-content: center;
  margin-top: 0.35rem;
`

export const EditMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  // border-top: 1px solid rgba(42, 46, 51, 0.1);
  font-size: 13px;

  .Checkbox input[type="checkbox"] {
    width: 13px !important;
    height: 13px;
    flex-shrink: 0;
  }
`

export const MetaGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`
